import React, { Component } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import fixWebmDuration from "fix-webm-duration";

import UnsupportedView from "./defaults/unsupported-view";
import ErrorView from "./defaults/error-view";
import DisconnectedView from "./defaults/disconnected-view";
import { SwitchCameraView } from "./defaults/SwitchCameraView";
import renderActions from "./defaults/render-actions";
import getVideoInfo, { captureThumb } from "./get-video-info";
import {
  ReactVideoRecorderDataIssueError,
  ReactVideoRecorderRecordedBlobsUnavailableError,
  ReactVideoRecorderDataAvailableTimeoutError,
  ReactVideoRecorderMediaRecorderUnavailableError,
  ReactVideoRecorderDeviceUnavailableError,
} from "./custom-errors";

const MIME_TYPES = [
  'video/webm;codecs="vp8,opus"',
  "video/webm;codecs=h264",
  "video/webm;codecs=vp9",
  "video/webm",
  "video/mp4",
];

const CONSTRAINTS = {
  audio: true,
  video: true,
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  min-height: 300px;
  background-color: #000;
  color: white;
  box-sizing: border-box;
  * {
    box-sizing: inherit;
  }
`;

const CameraView = styled.div`
  width: 100%;
  height: 100%;
`;

const Video = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  min-height: 100%;
  min-width: 100%;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: translate(-50%, -50%) ${(props) => (props.isFlipped ? " scaleX(-1)" : "")}
    ${(props) => (props.rotate ? ` rotate(${props.rotate}deg)` : "")};
  ${(props) =>
    props.onClick &&
    css`
      cursor: pointer;
    `};
`;

export default class VideoRecorder extends Component {
  static propTypes = {
    /** Whether or not to start the camera initially */
    isOnInitially: PropTypes.bool,
    /** Whether or not to display the video flipped (makes sense for user facing camera) */
    isFlipped: PropTypes.bool,
    /** Pass this if you want to force a specific mime-type for the video */
    mimeType: PropTypes.string,
    /** How much time to wait until it starts recording (in ms) */
    countdownTime: PropTypes.number,
    /** Use this if you want to set a time limit for the video (in ms) */
    timeLimit: PropTypes.number,
    /** Use this if you want to show play/pause/etc. controls on the replay video */
    showReplayControls: PropTypes.bool,
    /** Use this to turn off autoplay and looping of the replay video. It is recommended to also showReplayControls in order to play */
    replayVideoAutoplayAndLoopOff: PropTypes.bool,
    /** Use this if you want to customize the constraints passed to getUserMedia() */
    constraints: PropTypes.shape({
      audio: PropTypes.any,
      video: PropTypes.any,
    }),
    chunkSize: PropTypes.number,
    dataAvailableTimeout: PropTypes.number,
    useVideoInput: PropTypes.bool,
    /** Use this to configure the replaying video element's controlslist attribute */
    videoControlsList: PropTypes.string,
    /** Use this to disable picture in picture mode on the replaying video element */
    disablePictureInPicture: PropTypes.bool,

    renderDisconnectedView: PropTypes.func,
    renderLoadingView: PropTypes.func,
    renderVideoInputView: PropTypes.func,
    renderUnsupportedView: PropTypes.func,
    renderErrorView: PropTypes.func,
    renderActions: PropTypes.func,

    cameraViewClassName: PropTypes.string,
    videoClassName: PropTypes.string,
    wrapperClassName: PropTypes.string,

    /** Use this to localize the texts */
    t: PropTypes.func,

    onCameraOn: PropTypes.func,
    onTurnOnCamera: PropTypes.func,
    onSwitchCamera: PropTypes.func,
    onTurnOffCamera: PropTypes.func,
    onStartRecording: PropTypes.func,
    onStopRecording: PropTypes.func,
    onPauseRecording: PropTypes.func,
    onResumeRecording: PropTypes.func,
    onRecordingComplete: PropTypes.func,
    onOpenVideoInput: PropTypes.func,
    onStopReplaying: PropTypes.func,
    onUpload: PropTypes.func,
    onError: PropTypes.func,
  };

  static defaultProps = {
    renderUnsupportedView: () => <UnsupportedView />,
    renderErrorView: ({ error }) => <ErrorView error={error} />,
    renderVideoInputView: ({ videoInput }) => <>{videoInput}</>,
    renderDisconnectedView: () => <DisconnectedView />,
    renderLoadingView: () => <></>,
    t: (x) => x,
    renderActions,
    isFlipped: true,
    countdownTime: 3000,
    constraints: CONSTRAINTS,
    chunkSize: 250,
    dataAvailableTimeout: 2000,
  };

  videoInput = React.createRef();

  isComponentUnmounted = false;

  timeSinceInactivity = 0;

  lastRecordingTimestamp = 0;
  recordingDuration = 0;

  state = {
    isFrontCamera: true,
    isRecording: false,
    isCameraOn: false,
    isConnecting: false,
    isReplayingVideo: false,
    isReplayVideoMuted: true,
    thereWasAnError: false,
    error: null,
    streamIsReady: false,
    isInlineRecordingSupported: null,
    isVideoInputSupported: null,
    stream: undefined,
    currentDeviceId: null,
    availableDeviceIds: [],
    replayVideoRotation: 0,
  };

  componentDidMount() {
    const isInlineRecordingSupported = !!window.MediaRecorder && !!navigator.mediaDevices;

    const isVideoInputSupported = document.createElement("input").capture !== undefined;

    this.setState(
      {
        isInlineRecordingSupported,
        isVideoInputSupported,
      },
      () => {
        if (this.props.useVideoInput && this.props.isOnInitially) {
          this.handleOpenVideoInput();
        } else if (this.state.isInlineRecordingSupported && this.props.isOnInitially) {
          this.turnOnCamera();
        } else if (this.state.isVideoInputSupported && this.props.isOnInitially) {
          this.handleOpenVideoInput();
        }
      },
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.replayVideo && this.state.isReplayingVideo && !prevState.isReplayingVideo) {
      this.tryToUnmuteReplayVideo();
    }

    if (this.state.isCameraOn !== prevState.isCameraOn && this.state.isCameraOn) {
      // Only if the cameraVideo ref is available
      if (window.URL && this.cameraVideo) {
        this.cameraVideo.srcObject = this.state.stream;
      } else {
        this.cameraVideo.src = this.state.stream;
      }
    }
  }

  componentWillUnmount() {
    this.turnOffCamera();
    this.isComponentUnmounted = true;
  }

  turnOnCamera = (deviceId = null) => {
    if (this.props.onTurnOnCamera) {
      this.props.onTurnOnCamera();
    }

    navigator.mediaDevices
      .enumerateDevices()
      .then((mediaDevices) => {
        const videoDevices = mediaDevices.filter((x) => x.kind === "videoinput");
        if (deviceId && videoDevices[0] && videoDevices.find((x) => x.deviceId) === undefined) {
          return this.handleError(new ReactVideoRecorderDeviceUnavailableError());
        }

        const currentDeviceId = typeof deviceId === "string" ? deviceId : videoDevices[0].deviceId;

        this.setState({
          isConnecting: true,
          isReplayingVideo: false,
          thereWasAnError: false,
          currentDeviceId,
          availableDeviceIds: videoDevices.map((x) => x.deviceId),
          error: null,
        });

        const fallbackContraints = {
          audio: true,
          video: true,
        };

        const currentConstraints = Object.assign(
          {
            video: {
              deviceId: {
                exact: currentDeviceId,
              },
            },
          },
          this.props.constraints,
        );

        navigator.mediaDevices
          .getUserMedia(currentConstraints)
          .catch((err) => {
            // there's a bug in chrome in some windows computers where using `ideal` in the constraints throws a NotReadableError
            if (err.name === "NotReadableError" || err.name === "OverconstrainedError") {
              console.warn(`Got ${err.name}, trying getUserMedia again with fallback constraints`);
              return navigator.mediaDevices.getUserMedia(fallbackContraints);
            }
            // Add specific handling for permission denied errors
            if (err.name === "NotAllowedError") {
              console.warn("Camera permission denied by user");
              // Create a more specific error with user-friendly message
              const permissionError = new Error("Camera permission denied. Please enable camera access in your browser settings and try again.");
              permissionError.name = "NotAllowedError";
              permissionError.originalError = err;
              throw permissionError;
            }
            throw err;
          })
          .then(this.handleSuccess)
          .catch(this.handleError);
      })
      .catch(this.handleError);
  };

  handleSwitchCamera = () => {
    this.setState({
      isConnecting: true,
    });

    if (this.props.onSwitchCamera) {
      this.props.onSwitchCamera();
    }
    // Stop media tracks
    this.stream && this.stream.getTracks().forEach((stream) => stream.stop());

    navigator.mediaDevices.enumerateDevices().then((mediaDevices) => {
      const videoDevices = mediaDevices.filter((x) => x.kind === "videoinput");
      if (videoDevices.length === 0) {
        return this.handleError(new ReactVideoRecorderDeviceUnavailableError());
      }

      // Device that contains the word "front" in the label or the first device
      const frontCamera = videoDevices.find((d) => d.label.toLowerCase().includes("front")) || videoDevices[0];
      const frontCameraId = frontCamera ? frontCamera.deviceId : null;

      // Device that contains the word "back" in the label or the second device (or default to first if only one camera exists)
      const backCamera =
        videoDevices.find((d) => d.label.toLowerCase().includes("back")) ||
        (videoDevices.length > 0 ? videoDevices[1] : videoDevices[0]);
      const backCameraId = backCamera ? backCamera.deviceId : null;

      if (!frontCamera || !backCamera) {
        return this.handleError(new ReactVideoRecorderDeviceUnavailableError());
      }

      const deviceId = this.state.isFrontCamera ? backCameraId : frontCameraId;
      this.setState({
        isFrontCamera: !this.state.isFrontCamera,
      });

      this.turnOffCamera();
      return this.turnOnCamera(deviceId);
    });
  };

  turnOffCamera = () => {
    if (this.props.onTurnOffCamera) {
      this.props.onTurnOffCamera();
    }

    this.stream && this.stream.getTracks().forEach((stream) => stream.stop());
    this.setState({
      isCameraOn: false,
    });
    clearInterval(this.inactivityTimer);
  };

  handleSuccess = (stream) => {
    // Since handleSuccess is an async function, we may be in a situation where this was called after the
    // component was unmounted
    if (this.isComponentUnmounted) {
      return;
    }

    this.stream = stream;
    this.setState({
      stream: stream,
      isCameraOn: true,
    });
    if (this.props.onCameraOn) {
      this.props.onCameraOn();
    }

    // there is probably a better way
    // but this makes sure the start recording button
    // gives the stream a couple miliseconds to be ready
    setTimeout(() => {
      this.setState({
        isConnecting: false,
        streamIsReady: true,
      });
    }, 200);
  };

  handleError = (err) => {
    const { onError } = this.props;

    if (onError) {
      onError(err);
    }

    if (this.isComponentUnmounted) {
      return;
    }

    console.error("Captured error", err);

    clearTimeout(this.timeLimitTimeout);

    this.setState({
      isConnecting: this.state.isConnecting && false,
      isRecording: false,
      thereWasAnError: true,
      error: err,
    });

    if (this.state.isCameraOn) {
      this.turnOffCamera();
    }
  };

  handleDataIssue = (event) => {
    const error = new ReactVideoRecorderDataIssueError(event);
    console.error(error.message, event);
    this.handleError(error);
    return false;
  };

  getMimeType = () => {
    if (this.props.mimeType) {
      return this.props.mimeType;
    }

    const mimeType = window.MediaRecorder.isTypeSupported
      ? MIME_TYPES.find(window.MediaRecorder.isTypeSupported)
      : "video/webm";

    return (this.mediaRecorder && this.mediaRecorder.mimeType) || mimeType || "";
  };

  isDataHealthOK = (event) => {
    if (!event.data) return this.handleDataIssue(event);

    const { chunkSize } = this.props;

    const dataCheckInterval = 2000 / chunkSize;

    // in some browsers (FF/S), data only shows up
    // after a certain amount of time ~everyt 2 seconds
    const blobCount = this.recordedBlobs.length;
    if (blobCount > dataCheckInterval && blobCount % dataCheckInterval === 0) {
      const blob = new window.Blob(this.recordedBlobs, {
        type: this.getMimeType(),
      });
      if (blob.size <= 0) return this.handleDataIssue(event);
    }

    return true;
  };

  tryToUnmuteReplayVideo = () => {
    const video = this.replayVideo;
    video.muted = false;

    const playPromise = video.play();
    if (!playPromise) {
      video.muted = true;
      return;
    }

    playPromise
      .then(() => {
        this.setState({ isReplayVideoMuted: false });
        // fixes bug where seeking control during autoplay is not available until the video is almost completely played through
        if (this.props.replayVideoAutoplayAndLoopOff) {
          // Use setTimeout to avoid interrupting the play() call
          setTimeout(() => {
            video.pause();
            video.loop = false;
          }, 100);
        }
      })
      .catch((err) => {
        // Filter out AbortError which is expected when play/pause race occurs
        if (err.name !== "AbortError") {
          console.warn("Could not autoplay replay video", err);
        }
        video.muted = true;
        return video.play();
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.warn("Could play muted replay video after failed autoplay", err);
        }
      });
  };

  handleDataAvailable = (event) => {
    if (this.isDataHealthOK(event)) {
      this.recordedBlobs.push(event.data);
    }
  };

  getTotalEllapsedTimeInMs = () => Date.now() - this.lastRecordingTimestamp + this.recordingDuration;

  handleStopRecording = () => {
    if (this.props.onStopRecording) {
      this.props.onStopRecording();
    }

    if (!this.mediaRecorder) {
      this.handleError(new ReactVideoRecorderMediaRecorderUnavailableError());
      return;
    }

    this.mediaRecorder.stop();
    this.recordingDuration = this.getTotalEllapsedTimeInMs();
  };

  handlePauseRecording = () => {
    if (this.props.onPauseRecording) {
      this.props.onPauseRecording();
    }

    if (!this.mediaRecorder) {
      this.handleError(new ReactVideoRecorderMediaRecorderUnavailableError());
      return;
    }

    this.mediaRecorder.pause();
    this.recordingDuration = this.getTotalEllapsedTimeInMs();
  };

  handleResumeRecording = () => {
    if (this.props.onResumeRecording) {
      this.props.onResumeRecording();
    }

    if (!this.mediaRecorder) {
      this.handleError(new ReactVideoRecorderMediaRecorderUnavailableError());
      return;
    }

    this.mediaRecorder.resume();
    this.lastRecordingTimestamp = Date.now();
  };

  handleStartRecording = () => {
    if (this.props.onStartRecording) {
      this.props.onStartRecording();
    }

    this.setState({
      isRunningCountdown: true,
      isReplayingVideo: false,
    });

    setTimeout(() => this.startRecording(), this.props.countdownTime);
  };

  startRecording = () => {
    captureThumb(this.cameraVideo).then((thumbnail) => {
      this.thumbnail = thumbnail;

      this.recordedBlobs = [];
      const options = {
        mimeType: this.getMimeType(),
      };

      try {
        this.setState({
          isRunningCountdown: false,
          isRecording: true,
        });
        this.startedAt = new Date().getTime();
        this.mediaRecorder = new window.MediaRecorder(this.stream, options);
        this.mediaRecorder.addEventListener("stop", this.handleStop);
        this.mediaRecorder.addEventListener("error", this.handleError);
        this.mediaRecorder.addEventListener("dataavailable", this.handleDataAvailable);

        const { timeLimit, chunkSize, dataAvailableTimeout } = this.props;
        this.mediaRecorder.start(chunkSize); // collect 10ms of data
        this.lastRecordingTimestamp = Date.now();
        this.recordingDuration = 0;

        if (timeLimit) {
          this.timeLimitTimeout = setTimeout(() => {
            this.handleStopRecording();
          }, timeLimit);
        }

        // Only check for data availability if we're not using a time limit
        if (Number.isInteger(dataAvailableTimeout) && !timeLimit) {
          setTimeout(() => {
            if (this.recordedBlobs.length === 0) {
              this.handleError(new ReactVideoRecorderDataAvailableTimeoutError(dataAvailableTimeout));
            }
          }, dataAvailableTimeout);
        }
      } catch (err) {
        console.error("Couldn't create MediaRecorder", err, options);
        this.handleError(err);
      }
    });
  };

  handleStop = (event) => {
    const endedAt = new Date().getTime();

    if (!this.recordedBlobs || this.recordedBlobs.length <= 0) {
      const error = new ReactVideoRecorderRecordedBlobsUnavailableError(event);
      console.error(error.message, event);
      this.handleError(error);
      return;
    }

    clearTimeout(this.timeLimitTimeout);

    const videoBlob =
      this.recordedBlobs.length === 1
        ? this.recordedBlobs[0]
        : new window.Blob(this.recordedBlobs, {
            type: this.getMimeType(),
          });

    const thumbnailBlob = this.thumbnail;
    const startedAt = this.startedAt;
    const duration = endedAt - startedAt;

    // if this gets executed too soon, the last chunk of data is lost on FF
    this.mediaRecorder.ondataavailable = null;

    this.fixVideoMetadata(videoBlob).then((fixedVideoBlob) => {
      this.setState({
        isRecording: false,
        isReplayingVideo: true,
        isReplayVideoMuted: true,
        fixedVideoBlob,
        videoUrl: window.URL.createObjectURL(fixedVideoBlob),
      });

      this.turnOffCamera();

      this.props.onRecordingComplete(fixedVideoBlob, startedAt, thumbnailBlob, duration);
    });
  };

  // see https://bugs.chromium.org/p/chromium/issues/detail?id=642012
  fixVideoMetadata = (rawVideoBlob) => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      return Promise.resolve(rawVideoBlob);
    }
    // see https://stackoverflow.com/a/63568311
    Blob.prototype.arrayBuffer ??= function () {
      return new Response(this).arrayBuffer();
    };

    return fixWebmDuration(rawVideoBlob, this.recordingDuration);
  };

  handleVideoSelected = (e) => {
    if (this.state.isReplayingVideo) {
      this.setState({
        isReplayingVideo: false,
      });
    }

    const files = e.target.files || e.dataTransfer.files;
    if (files.length === 0) return;

    const startedAt = new Date().getTime();
    const video = files[0];

    e.target.value = null;

    const extension = video.type === "video/quicktime" ? "mov" : undefined;

    getVideoInfo(video)
      .then(({ duration, thumbnail }) => {
        this.setState({
          isRecording: false,
          isReplayingVideo: true,
          isReplayVideoMuted: true,
          videoBlob: video,
          videoUrl: window.URL.createObjectURL(video),
        });

        this.props.onRecordingComplete(video, startedAt, thumbnail, duration, extension);
      })
      .catch((err) => {
        this.handleError(err);
      });
  };

  handleOpenVideoInput = () => {
    if (this.props.onOpenVideoInput) {
      this.props.onOpenVideoInput();
    }

    this.videoInput.current.value = null;
    this.videoInput.current.click();

    // fixes a bug on iPhone where it doesn't save the recorded video on the second time (if you press the 'Use another video' button)
    this.videoInput.current.addEventListener("change", this.handleVideoSelected);
  };

  handleStopReplaying = () => {
    if (this.props.onStopReplaying) {
      this.props.onStopReplaying();
    }

    if (this.props.useVideoInput && this.props.isOnInitially) {
      return this.handleOpenVideoInput();
    }

    this.setState({
      isReplayingVideo: false,
    });

    if (this.state.isInlineRecordingSupported && this.props.isOnInitially) {
      this.turnOnCamera();
    } else if (this.state.isVideoInputSupported && this.props.isOnInitially) {
      this.handleOpenVideoInput();
    }
  };

  handleReplayVideoClick = () => {
    if (this.replayVideo.paused && !this.props.showReplayControls) {
      this.replayVideo.play().catch((err) => {
        // Silently handle AbortError which can occur from rapid play/pause calls
        if (err.name !== "AbortError") {
          console.warn("Error playing video on click", err);
        }
      });
    }

    // fixes bug where seeking control during autoplay is not available until the video is almost completely played through
    if (!this.props.replayVideoAutoplayAndLoopOff) {
      this.setState({
        isReplayVideoMuted: !this.state.isReplayVideoMuted,
      });
    }
  };

  handleReplayVideoLoadedMetadata = (e) => {
    const video = e.target;
    if (video.videoHeight > video.videoWidth) {
      this.setState({ replayVideoRotation: 90 });
    } else {
      this.setState({ replayVideoRotation: 0 });
    }
  };

  renderCameraView() {
    const {
      cameraViewClassName,
      showReplayControls,
      videoControlsList,
      disablePictureInPicture,
      replayVideoAutoplayAndLoopOff,
      renderDisconnectedView,
      renderVideoInputView,
      renderUnsupportedView,
      renderErrorView,
      renderLoadingView,
      useVideoInput,
      videoClassName,
      showSubmitButton,
    } = this.props;

    const {
      isVideoInputSupported,
      isReplayingVideo,
      isInlineRecordingSupported,
      thereWasAnError,
      error,
      isCameraOn,
      isConnecting,
      isReplayVideoMuted,
      isRecording,
    } = this.state;

    const shouldUseVideoInput = useVideoInput || (!isInlineRecordingSupported && isVideoInputSupported);

    const videoInput = shouldUseVideoInput ? (
      <input
        ref={this.videoInput}
        key="videoInput"
        type="file"
        accept="video/*"
        capture={useVideoInput ? undefined : "user"}
        style={{ display: "none" }}
        onChange={this.handleVideoSelected}
      />
    ) : null;

    if (isReplayingVideo) {
      return (
        <CameraView key="replay" className={cameraViewClassName}>
          <Video
            ref={(el) => (this.replayVideo = el)}
            className={videoClassName}
            src={this.state.videoUrl}
            loop
            muted={isReplayVideoMuted}
            playsInline
            autoPlay={!replayVideoAutoplayAndLoopOff}
            controls={showReplayControls}
            onClick={this.handleReplayVideoClick}
            onDurationChange={this.handleDurationChange}
            onLoadedMetadata={this.handleReplayVideoLoadedMetadata}
            controlsList={videoControlsList}
            disablePictureInPicture={disablePictureInPicture}
          />
          {videoInput}
        </CameraView>
      );
    }

    if (shouldUseVideoInput) {
      return renderVideoInputView({ videoInput });
    }

    if (!isInlineRecordingSupported) {
      return renderUnsupportedView();
    }

    if (thereWasAnError) {
      return renderErrorView({ error });
    }

    if (isCameraOn) {
      return (
        <CameraView key="camera">
          <Video
            isFlipped={this.props.isFlipped}
            ref={(el) => (this.cameraVideo = el)}
            autoPlay
            muted
            playsInline
            rotate={0}
          />
          {!isRecording && <SwitchCameraView onClick={this.handleSwitchCamera} />}
        </CameraView>
      );
    }

    if (isConnecting) {
      return renderLoadingView();
    }

    return renderDisconnectedView();
  }

  render() {
    const {
      isVideoInputSupported,
      isInlineRecordingSupported,
      thereWasAnError,
      isRecording,
      isCameraOn,
      streamIsReady,
      isConnecting,
      isRunningCountdown,
      isReplayingVideo,
      isReplayVideoMuted,
    } = this.state;

    const {
      countdownTime,
      timeLimit,
      showReplayControls,
      replayVideoAutoplayAndLoopOff,
      renderActions,
      showSubmitButton,
      t,
      useVideoInput,
      uploaded,
      uploading,
      onUpload,
      wrapperClassName,
    } = this.props;

    return (
      <Wrapper className={wrapperClassName}>
        {this.renderCameraView()}
        {renderActions({
          t,
          isVideoInputSupported,
          isInlineRecordingSupported,
          thereWasAnError,
          isRecording,
          isCameraOn,
          streamIsReady,
          isConnecting,
          isRunningCountdown,
          isReplayingVideo,
          isReplayVideoMuted,
          countdownTime,
          timeLimit,
          showReplayControls,
          replayVideoAutoplayAndLoopOff,
          useVideoInput,

          onTurnOnCamera: () => this.turnOnCamera(),
          onSwitchCamera: this.handleSwitchCamera,
          onTurnOffCamera: this.turnOffCamera,
          onOpenVideoInput: this.handleOpenVideoInput,
          onStartRecording: this.handleStartRecording,
          onStopRecording: this.handleStopRecording,
          onPauseRecording: this.handlePauseRecording,
          onResumeRecording: this.handleResumeRecording,
          onStopReplaying: this.handleStopReplaying,
          onUpload: onUpload,
          uploaded: uploaded,
          uploading: uploading,
          showSubmitButton: showSubmitButton,
        })}
      </Wrapper>
    );
  }
}
